import React, { useEffect, useRef, useState } from 'react';
import { AlertCircle, CheckCircle, ChevronDownIcon, ChevronUpIcon, Clock, ShieldCheckIcon, SmartphoneIcon, WalletIcon } from 'lucide-react';
import { payvayGuideSteps } from '../../data/payvayGuide';
import { formatPrice } from '../../utils/formatters';
interface PayVayWalletPaymentProps {
  totalAmount: number;
  onComplete: () => void;
  onCancel: () => void;
  initialStep?: 'input' | 'waiting';
}
type WalletStep = 'input' | 'waiting';
const PAYVAY_LOGO = 'https://cdn.magicpatterns.com/uploads/vLecFTCvQiPVbcveGEWCvz/PayVay1.png';
export function PayVayWalletPayment({
  totalAmount,
  onComplete,
  onCancel,
  initialStep
}: PayVayWalletPaymentProps) {
  const [step, setStep] = useState<WalletStep>(initialStep ?? 'input');
  const [walletNumber, setWalletNumber] = useState(initialStep === 'waiting' ? '03001234567' : '');
  const [error, setError] = useState('');
  const [timeLeft, setTimeLeft] = useState(300);
  const [isExpired, setIsExpired] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setWalletNumber(e.target.value.replace(/\D/g, '').slice(0, 11));
    setError('');
  };
  const handleContinue = (e: React.FormEvent) => {
    e.preventDefault();
    if (walletNumber.length !== 11) {
      setError('Wallet number must be exactly 11 digits');
      return;
    }
    if (!walletNumber.startsWith('03')) {
      setError('Wallet number must start with 03');
      return;
    }
    setTimeLeft(300);
    setIsExpired(false);
    setStep('waiting');
  };

  // 5-minute countdown
  useEffect(() => {
    if (step !== 'waiting') return;
    if (timeLeft <= 0) {
      setIsExpired(true);
      return;
    }
    const timerId = setInterval(() => setTimeLeft(prev => prev - 1), 1000);
    return () => clearInterval(timerId);
  }, [step, timeLeft]);

  // Simulated approval after 50 seconds
  useEffect(() => {
    if (step !== 'waiting' || isExpired) return;
    const successTimerId = setTimeout(() => onCompleteRef.current(), 50000);
    return () => clearTimeout(successTimerId);
  }, [step, isExpired]);
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  return <div className="flex flex-col py-2" data-id="element-540" data-mp="769dnu6yoh1a">
      <div className="text-center mb-6" data-id="element-541" data-mp="33cqrrae1yrj">
        <img src={PAYVAY_LOGO} alt="PayVay" className="h-10 object-contain mx-auto mb-2" data-id="element-542" data-mp="32bfblpgna9a" />
        
      </div>

      {step === 'input' && <form onSubmit={handleContinue} className="space-y-6 max-w-sm mx-auto w-full" data-id="element-543" data-mp="c39jc3zwfhrv">
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 flex justify-between items-center" data-id="element-544" data-mp="c1gf0vcrd9n1">
            <div data-id="element-545" data-mp="2dumfo4nswhcb">
              <p className="text-sm text-blue-800 font-medium" data-id="element-546" data-mp="2dv5yrop6tbrx">
                Payment Amount
              </p>
              <p className="text-xl font-bold text-blue-900" data-id="element-547" data-mp="tdmws1wjlgwz">
                {formatPrice(totalAmount)}
              </p>
            </div>
            <p className="text-xs text-blue-600 uppercase tracking-wider font-semibold" data-id="element-548" data-mp="ydre1u59yfo9">
              Gadgets Zone
            </p>
          </div>

          <div data-id="element-549" data-mp="ol3l5ahf0vut">
            <label htmlFor="payvayWallet" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-550" data-mp="okkdqnpe5pv1">
              PayVay Wallet Number
            </label>
            <div className="relative" data-id="element-551" data-mp="fl72bphpir7o">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-552" data-mp="fm8gw3xxlvu5">
                <WalletIcon className="h-5 w-5 text-gray-400" data-id="element-553" data-mp="p7w6ke5r156" />
              </div>
              <input id="payvayWallet" type="tel" inputMode="numeric" value={walletNumber} onChange={handleChange} maxLength={11} placeholder="03XXXXXXXXX" aria-invalid={!!error} aria-describedby="payvayWallet-help" className={`w-full pl-10 pr-4 py-3 border rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 font-mono tracking-wide ${error ? 'border-red-500' : 'border-gray-300'}`} data-id="element-554" data-mp="5m7si3l7gpj3" />
            </div>
            {error && <p className="mt-1 text-sm text-red-600" role="alert" data-id="element-555" data-mp="3s339oiks0ma4">
                {error}
              </p>}
            <p id="payvayWallet-help" className="mt-2 text-xs text-gray-500" data-id="element-556" data-mp="3n1no8peosdr7">
              Enter your 11-digit mobile number starting with 03
            </p>
          </div>

          <div className="pt-2 flex flex-col space-y-3" data-id="element-557" data-mp="3d6chat9isau0">
            <button type="submit" disabled={!walletNumber} className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-colors" data-id="element-558" data-mp="38em5sjyujh10">
              Continue
            </button>
            <button type="button" onClick={onCancel} className="w-full flex justify-center py-3 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-559" data-mp="2ue1ojs8kxt9m">
              Cancel
            </button>
          </div>
        </form>}

      {step === 'waiting' && <div className="text-center max-w-sm mx-auto w-full" data-id="element-560" data-mp="2kcpfm3zy9m06">
          {isExpired ? <div className="bg-red-50 border border-red-200 rounded-xl p-8 flex flex-col items-center" data-id="element-561" data-mp="2paqz2q1afnc6">
              <AlertCircle className="h-12 w-12 text-red-500 mb-4" data-id="element-562" data-mp="2u6zmsrtjwouy" />
              <h3 className="text-lg font-bold text-red-900 mb-2" data-id="element-563" data-mp="1wmmt03et188y">
                Request Expired
              </h3>
              <p className="text-sm text-red-700 mb-6 text-center" data-id="element-564" data-mp="21kobon8bj5jw">
                The payment request was not approved in time. Please try again.
              </p>
              <button onClick={() => setStep('input')} className="w-full py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors" data-id="element-565" data-mp="2bag71jo8s2pf">
                Try Again
              </button>
            </div> : <div className="flex flex-col items-center py-4" data-id="element-566" data-mp="1at7m1n74v7ew">
              <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mb-6 relative" data-id="element-567" data-mp="1armpjs19deee">
                <div className="absolute inset-0 border-4 border-blue-200 rounded-full animate-ping opacity-75" data-id="element-568" data-mp="15s1s5m1qle3s"></div>
                <CheckCircle className="w-10 h-10 text-blue-600 relative z-10" data-id="element-569" data-mp="1fjxs72ndvean" />
              </div>

              <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-570" data-mp="rud9mkv56j1w">
                Request Sent Successfully
              </h3>
              <p className="text-sm text-gray-600 mb-6 px-4" data-id="element-571" data-mp="mw1zsa4uk8jo">
                A payment request of{' '}
                <span className="font-bold" data-id="element-572" data-mp="11tkkn1eem1lu">{formatPrice(totalAmount)}</span>{' '}
                has been sent to PayVay Wallet{' '}
                <span className="font-mono font-semibold" data-id="element-573" data-mp="ws4zzag51x3z">{walletNumber}</span>.
                Please approve it in your PayVay App.
              </p>

              <div className="flex items-center justify-center space-x-2 text-sm font-medium text-gray-700 bg-gray-100 px-6 py-3 rounded-full mb-6" data-id="element-574" data-mp="2894tg9e82hzl">
                <Clock className="h-5 w-5 text-blue-600" data-id="element-575" data-mp="2fu78belluuv2" />
                <span data-id="element-576" data-mp="2ks8qlxb7iq5h">
                  Expires in:{' '}
                  <span className="text-blue-600 font-bold text-base" data-id="element-577" data-mp="26rftd4titgyo">
                    {formatTime(timeLeft)}
                  </span>
                </span>
              </div>

              {/* How to Pay guidelines */}
              <div className="w-full bg-gray-50 border border-gray-200 rounded-xl mb-6 overflow-hidden text-left" data-id="element-578" data-mp="2yi3vaj30qqde">
                <button type="button" onClick={() => setShowGuide(!showGuide)} aria-expanded={showGuide} className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-colors" data-id="element-579" data-mp="33gnfady1xs34">
                  <span className="flex items-center gap-2" data-id="element-580" data-mp="2tpssn8gw8qh5">
                    <SmartphoneIcon className="w-4 h-4 text-blue-600" data-id="element-581" data-mp="3hgez4vb922p1" />
                    How to pay with PayVay?
                  </span>
                  {showGuide ? <ChevronUpIcon className="w-4 h-4 text-gray-500" data-id="element-582" data-mp="2xhk1ez7svlao" /> : <ChevronDownIcon className="w-4 h-4 text-gray-500" data-id="element-583" data-mp="2xibyyfkyjt3l" />}
                </button>
                {showGuide && <ol className="px-4 pb-4 pt-3 border-t border-gray-200 space-y-4" data-id="element-584" data-mp="2sp8zxrxevrg0">
                    {payvayGuideSteps.map((guideStep, index) => {
              const isLast = index === payvayGuideSteps.length - 1;
              return <li key={guideStep.title} className="flex items-start gap-3" data-id="element-585" data-mp="2iqtaruhy2s95" data-mpi={index}>
                          <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${isLast ? 'bg-green-100' : 'bg-blue-100'}`} data-id="element-586" data-mp="3gfmupq7ds4ea">
                            {isLast ? <ShieldCheckIcon className="w-4 h-4 text-green-600" data-id="element-587" data-mp="36gxcfwbkohxi" /> : <span className="text-xs font-bold text-blue-700" data-id="element-588" data-mp="31je0j32ckh8q">
                                {index + 1}
                              </span>}
                          </div>
                          <div data-id="element-589" data-mp="1q3yu78o90d0y">
                            <p className="text-sm font-medium text-gray-800" data-id="element-590" data-mp="1q3pag2tsr23p">
                              {guideStep.title}
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5" data-id="element-591" data-mp="5k3fpo8m700c">
                              {guideStep.description}
                            </p>
                          </div>
                        </li>;
            })}
                  </ol>}
              </div>

              <p className="text-xs text-gray-500 animate-pulse" data-id="element-592" data-mp="kc5aitvtv2ti">
                Waiting for your approval...
              </p>

              <button onClick={onCancel} className="mt-6 text-sm text-gray-500 hover:text-gray-700 underline" data-id="element-593" data-mp="tbiljqzlnzgc">
                Cancel Payment
              </button>
            </div>}
        </div>}
    </div>;
}